const DISPLAY_LOCALE_FALLBACK = 'en-GB';
const MERIDIEM_LOCALE = 'en-US';

function toDisplayLocale(language?: string) {
  if (!language) return DISPLAY_LOCALE_FALLBACK;
  return language.startsWith('en') ? DISPLAY_LOCALE_FALLBACK : language;
}

export type ClockTime = {
  value: string;
  meridiem?: string;
};

/** "15:00" → {value: "3:00", meridiem: "PM"} in English, "15:00" elsewhere. */
export function formatClockTime(
  time?: string | null,
  language?: string,
): ClockTime | null {
  if (!time) return null;

  const [hours, minutes] = time.split(':');
  const hour = Number(hours);
  const minute = Number(minutes);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;

  const date = new Date(Date.UTC(2000, 0, 1, hour, minute));
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  };
  const locale = language?.startsWith('en') ? MERIDIEM_LOCALE : toDisplayLocale(language);

  const build = (resolved: string): ClockTime => {
    const parts = new Intl.DateTimeFormat(resolved, options).formatToParts(date);

    return {
      value: parts
        .filter(part => part.type !== 'dayPeriod')
        .map(part => part.value)
        .join('')
        .trim(),
      meridiem: parts.find(part => part.type === 'dayPeriod')?.value,
    };
  };

  try {
    return build(locale);
  } catch {
    return build(MERIDIEM_LOCALE);
  }
}

export function formatClockLabel(time?: string | null, language?: string): string | null {
  const clock = formatClockTime(time, language);
  if (!clock) return null;
  return clock.meridiem ? `${clock.value} ${clock.meridiem}` : clock.value;
}
